import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Spinner from '../../components/ui/Spinner';
import { useAuth } from './AuthProvider';
import ChangePasswordPage from './ChangePasswordPage';

/**
 * Wraps protected routes. While the auth state is loading it renders a
 * spinner (never a blank flash). Once resolved, redirects unauthenticated
 * users to /login, preserving the original destination via state.from.
 *
 * Seeded staff share a default password and carry mustChangePassword:true —
 * for those users this renders ChangePasswordPage instead of children,
 * blocking AppShell/nav until the password is changed.
 */
export default function RequireAuth({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-label="Checking authentication"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
        }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.mustChangePassword) {
    return <ChangePasswordPage />;
  }

  return <>{children}</>;
}
